/**
 * The KPI band at the top of the Cloud board.
 *
 * The first tile is the one that matters most: subscriptions *reachable* out of
 * subscriptions *expected*. Azure Resource Graph only ever returns what the
 * caller can read, so without that ratio a permissions regression would render
 * as a smaller, healthier-looking estate. When it is short, the tile goes
 * danger and says so.
 */

import { Card, SegmentBar, Statistic } from "@norican/react";

import type { CloudEstateSummary, WorkloadSummary } from "@/lib/api";

import {
  formatCount,
  formatMoney,
  STATE_COLOR,
  STATE_LABEL,
  STATE_ORDER,
  type WorkloadState,
} from "./status";

interface EstateSummaryProps {
  summary: CloudEstateSummary;
  workloads: WorkloadSummary;
}

export function EstateSummary({ summary, workloads }: EstateSummaryProps) {
  const allSubscriptionsVisible =
    summary.subscriptions_reachable === summary.subscriptions_expected;
  const missing = summary.subscriptions_expected - summary.subscriptions_reachable;

  const counts: Record<WorkloadState, number> = {
    running: workloads.running,
    stopped: workloads.stopped,
    provisioning: workloads.provisioning,
    failed: workloads.failed,
    unknown: workloads.unknown,
  };
  // Only show states that actually occur — an empty segment is just noise.
  const segments = STATE_ORDER.filter((state) => counts[state] > 0).map((state) => ({
    value: counts[state],
    label: `${STATE_LABEL[state]} (${counts[state]})`,
    color: STATE_COLOR[state],
  }));

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card
          variant="outline"
          accent="left"
          accentTone={allSubscriptionsVisible ? "success" : "danger"}
        >
          <Card.Body>
            <Statistic
              label="Subscriptions visible"
              value={`${summary.subscriptions_reachable} / ${summary.subscriptions_expected}`}
              tone={allSubscriptionsVisible ? "success" : "danger"}
              size="lg"
              caption={
                allSubscriptionsVisible
                  ? "Every expected subscription answered"
                  : `${missing} not readable — totals below are incomplete`
              }
            />
          </Card.Body>
        </Card>

        <Card
          variant="outline"
          accent="left"
          accentTone={
            summary.firing_alert_count == null
              ? "neutral"
              : summary.firing_alert_count > 0
                ? "danger"
                : "success"
          }
        >
          <Card.Body>
            <Statistic
              label="Alerts firing"
              value={formatCount(summary.firing_alert_count)}
              tone={
                summary.firing_alert_count == null
                  ? "neutral"
                  : summary.firing_alert_count > 0
                    ? "danger"
                    : "success"
              }
              size="lg"
              caption={
                summary.firing_alert_count == null
                  ? "Not collected yet — unknown is not zero"
                  : summary.firing_alert_count > 0
                    ? "Azure Monitor alerts firing right now"
                    : "No Azure Monitor alerts firing"
              }
            />
          </Card.Body>
        </Card>

        <Card variant="outline" accent="left" accentTone="accent">
          <Card.Body>
            <Statistic
              label="Resources"
              value={formatCount(summary.resources)}
              size="lg"
              caption={`${formatCount(summary.resource_groups)} resource groups · ${formatCount(
                summary.resource_types,
              )} types`}
            />
          </Card.Body>
        </Card>

        <Card
          variant="outline"
          accent="left"
          accentTone={workloads.stopped + workloads.failed > 0 ? "warning" : "success"}
        >
          <Card.Body>
            <Statistic
              label="Workloads running"
              value={`${workloads.running} / ${workloads.total}`}
              tone={workloads.stopped + workloads.failed > 0 ? "warning" : "success"}
              size="lg"
              caption={
                workloads.stopped + workloads.failed > 0
                  ? `${workloads.stopped} stopped · ${workloads.failed} failed`
                  : "Container apps, sites, databases and VMs"
              }
            />
          </Card.Body>
        </Card>

        <Card variant="outline" accent="left" accentTone="accent">
          <Card.Body>
            <Statistic
              label="Alert rules configured"
              value={formatCount(summary.alert_rules)}
              size="lg"
              caption="Existing Azure Monitor rules across the estate"
            />
          </Card.Body>
        </Card>

        {summary.month_to_date_cost != null && (
          <Card variant="outline" accent="left" accentTone="accent">
            <Card.Body>
              <Statistic
                label="Cost month to date"
                value={formatMoney(summary.month_to_date_cost, summary.cost_currency)}
                size="lg"
                caption="Actual Azure cost across the estate"
              />
            </Card.Body>
          </Card>
        )}
      </div>

      {segments.length > 0 && (
        <Card variant="subtle">
          <Card.Body>
            <p className="text-fg-muted mb-3 text-xs font-semibold tracking-wide uppercase">
              Workload composition
            </p>
            <SegmentBar
              segments={segments}
              size="lg"
              showLegend
              aria-label="Azure workloads by state"
            />
          </Card.Body>
        </Card>
      )}
    </div>
  );
}
